import { Icon } from '@iconify/react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BuildChip, Wordmark } from '../Landing';
import { clearSession } from '../api';
import { LangSwitch } from '../components/LangSwitch';
import { NotificationBell } from '../components/NotificationBell';
import { ShortcutsProvider } from '../components/Shortcuts';
import { BottomNav, DesktopTabs, type Tab } from '../components/ui/AppTabs';
import { t } from '../i18n';

/**
 * Four places, and the thing you do every morning is not one of them.
 *
 * There were seven tabs across the bottom of a phone: Home, Tasks, Quick
 * command, Staff, Routines, Expenses, Payroll. Seven is not a menu, it is a
 * list — and on a 390px screen each one got 55 pixels and a truncated label.
 *
 * Nothing was removed, only regrouped around how the house is actually run:
 *   Home     — the day, and the house rules
 *   Tasks    — today, and the week (routines are the same thing, repeating)
 *   People   — the team, and each person's pay, hours and loans
 *   Money    — what the house spent
 * Giving out a task is not a place at all — it is the round button in the
 * middle, under the thumb, because it is the one thing done every morning.
 * Speaking a command and laying out the week are both on the Tasks screen,
 * one tap from it.
 */
export function OwnerLayout() {
  const nav = useNavigate();
  // Built here, not at module scope: `t()` reads the language at the moment it
  // is called, and at import time that is before bootstrapLang() has run.
  const tabs: Tab[] = [
    { to: '/home', label: t('Home'), icon: 'home' },
    { to: '/tasks', label: t('Tasks'), icon: 'tasks' },
    { to: '/staff', label: t('People'), icon: 'staff' },
    { to: '/money', label: t('Money'), icon: 'money' },
  ];
  function signout() {
    clearSession();
    nav('/');
  }

  return (
    <div className="app-shell app-shell-with-bottom-nav">
      {/* Desktop only.
       *
       * On a 390px phone this bar was a wordmark truncated to "famana", a
       * build chip broken over two lines, a bell, a language dropdown, and the
       * words Profile and Sign out wrapping into each other — five pieces of
       * admin chrome above a design that opens with one sentence. His screens
       * carry their own headers; the phone gets theirs, not this.
       *
       * It stays on a laptop because the bottom-nav is `sm:hidden` and without
       * it there is no way off the page. Language and sign-out live on the
       * profile screen, which is where a phone reaches them.
       */}
      <header className="app-header hidden sm:block">
        <div className="app-container py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Link to="/home" aria-label={t('Home')} className="touch">
              <Wordmark compact className="!flex-row !gap-2" />
            </Link>
            <BuildChip />
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <NavLink
              to="/search"
              aria-label={t('Search')}
              className="touch inline-flex items-center justify-center rounded-md px-2 text-foreground/80"
            >
              <Icon icon="ph:magnifying-glass-bold" className="text-lg" />
            </NavLink>
            <NotificationBell />
            <LangSwitch />
            <NavLink
              to="/profile"
              className="touch inline-flex items-center justify-center rounded-md px-3 text-sm text-foreground/80 hover:text-foreground"
            >
              {t('Profile')}
            </NavLink>
            <button
              type="button"
              onClick={signout}
              className="touch inline-flex items-center justify-center rounded-md px-3 text-sm text-muted-foreground hover:text-foreground"
            >
              {t('Sign out')}
            </button>
          </div>
        </div>
      </header>

      {/* Top tab nav — desktop only. On mobile the bottom-nav replaces it. */}
      <DesktopTabs
        tabs={tabs}
        trailing={
          <NavLink
            to="/tasks?new=1"
            className="ms-auto my-1.5 flex items-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-white transition-opacity duration-150 hover:opacity-90"
          >
            <span className="text-lg leading-none">+</span>
            {t('New task')}
          </NavLink>
        }
      />

      <main className="app-container flex-1 py-6 sm:py-8">
        <Outlet />
      </main>

      {/* Bottom tab nav — mobile only. Two tabs, the action, two tabs.
          One action, not a menu of them. A sheet that only exists to offer
          three destinations is navigation wearing a modal's clothes, and it
          puts a decision between the owner and the thing he does every
          morning. Giving out the week and speaking a command both live on
          the Tasks screen, where they belong. */}
      <BottomNav
        tabs={tabs}
        center={{ label: t('New task'), onClick: () => nav('/tasks?new=1') }}
      />

      <ShortcutsProvider />
    </div>
  );
}
